/**
 * API Client for ReadingVox Backend
 */

import type { SentenceStreamResponse, AudioMapping, APIError, WidgetFeatureConfig, TranslateResponse } from '../types/api';

/** Error from a JSON API call, carrying HTTP status and server error code */
export class APIRequestError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'APIRequestError';
    this.status = status;
    this.code = code;
  }
}

// Server limits for POST /translate
export const TRANSLATE_MAX_SELECTION_CHARS = 2000;
const TRANSLATE_MAX_ITEMS = 200;
const TRANSLATE_MAX_TOTAL_CHARS = 30000;
import { debugLog } from './logger';

/** A window of sentences: [start, next) of `total` (see /sentence/stream) */
export interface StreamWindow {
  start: number;
  next: number;
  total: number;
}

export interface StreamWindowOptions {
  /** First sentence to generate (default 0) */
  start?: number;
  /** Only generate this many sentences; omit for the whole text */
  limit?: number;
  /** Called with the server's window info (only when `limit` is set) */
  onWindow?: (w: StreamWindow) => void;
}

function windowParams(opts: StreamWindowOptions): { start?: number; limit?: number } {
  return opts.limit ? { start: opts.start || 0, limit: opts.limit } : {};
}

function isWindowEvent(x: unknown): x is { window: StreamWindow } {
  const w = (x as { window?: StreamWindow } | null)?.window;
  return !!w && typeof w.next === 'number' && typeof w.total === 'number';
}

export class APIClient {
  private apiKey?: string;
  private licenseKey?: string;
  private studentEmail?: string;
  private apiEndpoint: string;
  private apiOrigin: string;
  private debug: boolean;
  /** Privacy mode (LMS presets): never leak full page URLs via Referer */
  private stripUrlParams: boolean;
  /** Detected LMS id, reported to /widget/config as ?lms= */
  private lms: string | null;

  constructor(
    auth: { apiKey?: string; licenseKey?: string; studentEmail?: string },
    apiEndpoint: string = '/api/v1',
    debug: boolean = false,
    options: { stripUrlParams?: boolean; lms?: string | null } = {}
  ) {
    this.stripUrlParams = !!options.stripUrlParams;
    this.lms = options.lms || null;
    this.apiKey = auth.apiKey;
    this.licenseKey = auth.licenseKey;
    this.studentEmail = auth.studentEmail;
    this.apiEndpoint = apiEndpoint;
    this.debug = debug;

    // Extract origin from apiEndpoint for resolving relative URLs
    try {
      this.apiOrigin = new URL(apiEndpoint).origin;
    } catch {
      // Relative endpoint — use current page origin
      this.apiOrigin = window.location.origin;
    }
  }

  /**
   * Resolve a potentially relative URL against the API server origin.
   * Ensures audio/mapping URLs work from extension content scripts
   * where the page origin differs from the API server.
   */
  private resolveUrl(url: string): string {
    if (!url) return url;
    // Already absolute
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    // Relative path — prepend API origin
    return `${this.apiOrigin}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  getAuthHeaders(): Record<string, string> {
    if (this.licenseKey) {
      const headers: Record<string, string> = { 'X-License-Key': this.licenseKey };
      if (this.studentEmail) headers['X-Student-Email'] = this.studentEmail;
      return headers;
    }
    if (this.apiKey) {
      return { 'X-API-Key': this.apiKey };
    }
    return {};
  }

  private isLoopbackEndpoint(): boolean {
    try {
      const hostname = new URL(this.apiEndpoint).hostname.toLowerCase();
      return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
    } catch {
      return false;
    }
  }

  private extensionRuntime(): any | null {
    const ext = (globalThis as any).chrome;
    if (!ext?.runtime?.connect) return null;
    return ext.runtime;
  }

  private async *streamSentencesViaExtension(
    text: string,
    voice: string,
    url: string,
    opts: StreamWindowOptions = {}
  ): AsyncGenerator<SentenceStreamResponse, void, unknown> {
    const runtime = this.extensionRuntime();
    if (!runtime) {
      throw new Error('Extension runtime unavailable');
    }

    const port = runtime.connect({ name: 'rv-sentence-stream' });
    const queue: SentenceStreamResponse[] = [];
    let done = false;
    let error: Error | null = null;
    let wake: (() => void) | null = null;

    const notify = () => {
      if (wake) {
        const resolve = wake;
        wake = null;
        resolve();
      }
    };

    port.onMessage.addListener((msg: any) => {
      if (msg?.type === 'data' && msg.payload) {
        queue.push(msg.payload as SentenceStreamResponse);
      } else if (msg?.type === 'error') {
        error = new Error(msg.error || 'Sentence stream failed');
        done = true;
      } else if (msg?.type === 'end') {
        done = true;
      }
      notify();
    });

    port.onDisconnect.addListener(() => {
      if (!done && !error) {
        error = new Error('Sentence stream disconnected');
        done = true;
        notify();
      }
    });

    const streamRequest = {
      endpoint: `${this.apiEndpoint}/sentence/stream`,
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      body: { text, voice, url, ...windowParams(opts) },
    };
    debugLog('[ReadingVox API] Sending port message:', streamRequest.endpoint, 'bodyLength:', text.length);
    port.postMessage({ type: 'START_SENTENCE_STREAM', request: streamRequest });

    try {
      while (!done || queue.length > 0) {
        if (queue.length === 0) {
          await new Promise<void>((resolve) => {
            wake = resolve;
          });
          continue;
        }

        const parsed = queue.shift()!;
        if (isWindowEvent(parsed)) { opts.onWindow?.(parsed.window); continue; }
        if (parsed.audioUrl) parsed.audioUrl = this.resolveUrl(parsed.audioUrl);
        if (parsed.mappingUrl) parsed.mappingUrl = this.resolveUrl(parsed.mappingUrl);
        yield parsed;
      }
    } finally {
      port.disconnect();
    }

    if (error) {
      throw error;
    }
  }

  /**
   * Stream sentences from server (Server-Sent Events)
   */
  async *streamSentences(
    text: string,
    voice: string,
    url: string = window.location.href,
    opts: StreamWindowOptions = {}
  ): AsyncGenerator<SentenceStreamResponse, void, unknown> {
    debugLog('[ReadingVox API] streamSentences called — endpoint:', this.apiEndpoint, 'voice:', voice, 'text length:', text.length);
    debugLog('[ReadingVox API] isLoopback:', this.isLoopbackEndpoint(), 'hasExtRuntime:', !!this.extensionRuntime());

    if (this.isLoopbackEndpoint() && this.extensionRuntime()) {
      debugLog('[ReadingVox API] → Routing via extension port (loopback + extension)');
      yield* this.streamSentencesViaExtension(text, voice, url, opts);
      return;
    }

    debugLog('[ReadingVox API] → Direct fetch to', `${this.apiEndpoint}/sentence/stream`);
    const response = await fetch(`${this.apiEndpoint}/sentence/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify({ text, voice, url, ...windowParams(opts) }),
      ...this.fetchPrivacy(),
    });

    if (!response.ok) {
      const error: APIError = await response.json();
      throw new Error(error.error || 'API request failed');
    }

    // Parse Server-Sent Events
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();

    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data:')) {
          const data = line.substring(5).trim();

          if (data.length === 0) continue;

          try {
            const parsed: SentenceStreamResponse = JSON.parse(data);
            if (isWindowEvent(parsed)) { opts.onWindow?.(parsed.window); continue; }
            // Resolve relative URLs against API origin
            if (parsed.audioUrl) parsed.audioUrl = this.resolveUrl(parsed.audioUrl);
            if (parsed.mappingUrl) parsed.mappingUrl = this.resolveUrl(parsed.mappingUrl);
            yield parsed;
          } catch (error) {
            console.error('Failed to parse SSE data:', error);
          }
        } else if (line.startsWith('event:')) {
          const event = line.substring(6).trim();

          if (event === 'complete') {
            return; // Stream complete
          } else if (event === 'error') {
            throw new Error('Server streaming error');
          }
        }
      }
    }
  }

  /**
   * GET {apiEndpoint}/widget/config — plan-driven feature flags + language list
   */
  async getWidgetConfig(): Promise<WidgetFeatureConfig> {
    const query = this.lms ? `?lms=${encodeURIComponent(this.lms)}` : '';
    return parseWidgetConfig(await this.requestJSON('GET', `/widget/config${query}`));
  }

  /**
   * Translate a single piece of text (selection, ≤2000 chars)
   */
  /** Plain-language bullet summary of a page or selection */
  async summarize(text: string): Promise<string[]> {
    const data = await this.requestJSON('POST', '/summarize', { text: text.slice(0, 12000) });
    if (!Array.isArray(data?.bullets)) throw new APIRequestError('Invalid summary response', 500);
    return data.bullets.map((b: unknown) => String(b));
  }

  async translateText(text: string, targetLang: string): Promise<{ translation: string; voice: string | null }> {
    const trimmed = text.slice(0, TRANSLATE_MAX_SELECTION_CHARS);
    const data: TranslateResponse = await this.requestJSON('POST', '/translate', {
      targetLang,
      sourceLang: 'en',
      text: trimmed,
    });
    if (typeof data?.translation !== 'string') {
      throw new APIRequestError('Invalid translation response', 500);
    }
    return { translation: data.translation, voice: data.voice ?? null };
  }

  /**
   * Translate many texts (full page). Batches to stay within server limits
   * (≤200 items and ≤30000 chars per request). Output order matches input.
   */
  async translateTexts(texts: string[], targetLang: string): Promise<{ translations: string[]; voice: string | null }> {
    const batches: string[][] = [];
    let current: string[] = [];
    let currentChars = 0;
    for (const raw of texts) {
      const t = raw.slice(0, TRANSLATE_MAX_TOTAL_CHARS);
      if (current.length > 0 && (current.length >= TRANSLATE_MAX_ITEMS || currentChars + t.length > TRANSLATE_MAX_TOTAL_CHARS)) {
        batches.push(current);
        current = [];
        currentChars = 0;
      }
      current.push(t);
      currentChars += t.length;
    }
    if (current.length > 0) batches.push(current);

    const translations: string[] = [];
    let voice: string | null = null;
    for (const batch of batches) {
      const data: TranslateResponse = await this.requestJSON('POST', '/translate', {
        targetLang,
        sourceLang: 'en',
        texts: batch,
      });
      if (!Array.isArray(data?.translations) || data.translations.length !== batch.length) {
        throw new APIRequestError('Invalid translation response', 500);
      }
      translations.push(...data.translations);
      voice = data.voice ?? null;
    }
    return { translations, voice };
  }

  /** Referrer policy for API calls in privacy mode (origin only, never path/query) */
  private fetchPrivacy(): RequestInit {
    return this.stripUrlParams ? { referrerPolicy: 'strict-origin' } : {};
  }

  /**
   * JSON request with auth headers. Throws APIRequestError on non-2xx.
   */
  /**
   * GET {apiEndpoint}/widget/i18n/<lang> — interface strings. No auth headers, so
   * it's a simple CORS request and cacheable per version.
   */
  async getUiStrings(lang: string, version: string): Promise<Record<string, string>> {
    const response = await fetch(`${this.apiEndpoint}/widget/i18n/${encodeURIComponent(lang)}?v=${encodeURIComponent(version)}`, {
      ...this.fetchPrivacy(),
    });
    if (!response.ok) throw new APIRequestError(`Request failed (${response.status})`, response.status);
    const data = await response.json();
    return data?.strings && typeof data.strings === 'object' ? data.strings : {};
  }

  /** POST {apiEndpoint}/document/text — a PDF's paragraphs for the document reader */
  async readDocument(url: string): Promise<{ title: string; pages: number; paragraphs: { page: number; text: string; heading?: boolean }[]; truncated: boolean }> {
    return this.requestJSON('POST', '/document/text', { url });
  }

  private async requestJSON(method: 'GET' | 'POST', path: string, body?: unknown): Promise<any> {
    const headers: Record<string, string> = { ...this.getAuthHeaders() };
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const response = await fetch(`${this.apiEndpoint}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...this.fetchPrivacy(),
    });

    let data: any = null;
    try {
      data = await response.json();
    } catch {
      // Non-JSON body
    }

    if (!response.ok) {
      throw new APIRequestError(data?.error || `Request failed (${response.status})`, response.status, data?.code);
    }
    return data;
  }

  /**
   * Fetch word timestamps mapping
   */
  async fetchMapping(mappingUrl: string): Promise<AudioMapping> {
    const response = await fetch(this.resolveUrl(mappingUrl));

    if (!response.ok) {
      throw new Error('Failed to fetch word mapping');
    }

    return await response.json();
  }

  /**
   * Log debug message
   */
  private log(...args: any[]) {
    if (this.debug) {
      console.log('[ReadingVox API]', ...args);
    }
  }
}

/** /widget/config response → WidgetFeatureConfig, keeping only well-formed fields */
export function parseWidgetConfig(data: any): WidgetFeatureConfig {
  const lms = data?.lms && typeof data.lms === 'object' ? data.lms : null;
  const voices = Array.isArray(data?.voices)
    ? data.voices
        .filter((v: any) => v && typeof v.id === 'string' && typeof v.name === 'string' && (v.gender === 'female' || v.gender === 'male'))
        .map((v: any) => ({ id: v.id, name: v.name, gender: v.gender, desc: typeof v.desc === 'string' ? v.desc : '' }))
    : undefined;
  return {
    lms: lms
      ? {
          detected: typeof lms.detected === 'string' ? lms.detected : null,
          licensed: lms.licensed !== false,
          message: typeof lms.message === 'string' ? lms.message : undefined,
          url: typeof lms.url === 'string' ? lms.url : undefined,
        }
      : undefined,
    branding: data?.branding !== false,
    selectionTranslation: !!data?.selectionTranslation,
    fullPageTranslation: !!data?.fullPageTranslation,
    languages: Array.isArray(data?.languages)
      ? data.languages
          .filter((l: any) => l && typeof l.code === 'string' && typeof l.name === 'string')
          .map((l: any) => ({ code: l.code, name: l.name, voice: typeof l.voice === 'string' && l.voice ? l.voice : null }))
      : [],
    // Plan voices (Free/Starter: Ava and Sam); absent from older servers
    voiceTier: data?.voiceTier === 'standard' || data?.voiceTier === 'premium' ? data.voiceTier : undefined,
    voices,
  };
}
